import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Chip,
  Divider,
  Avatar,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  Store as StoreIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

interface OfferStatusPopupProps {
  open: boolean;
  onClose: () => void;
  productName: string;
  productPrice: number;
  acceptedOfferPrice?: number;
  sellerName: string;
  reason: string;
}

const OfferStatusPopup: React.FC<OfferStatusPopupProps> = ({
  open,
  onClose,
  productName,
  productPrice,
  acceptedOfferPrice,
  sellerName,
  reason,
}) => {
  const getStatusInfo = () => {
    if (reason.includes('accepted offer')) {
      return {
        icon: <CheckIcon sx={{ fontSize: 40, color: 'success.main' }} />,
        title: 'Offer Already Accepted',
        description: 'This product already has an accepted offer from another buyer.',
        color: 'success',
        bgColor: 'success.50',
        borderColor: 'success.200',
      };
    } else if (reason.includes('no longer available')) {
      return {
        icon: <WarningIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
        title: 'Product Unavailable',
        description: 'This product is no longer available for offers.',
        color: 'warning',
        bgColor: 'warning.50',
        borderColor: 'warning.200',
      };
    } else if (reason.includes('own product')) {
      return {
        icon: <PersonIcon sx={{ fontSize: 40, color: 'info.main' }} />,
        title: 'Your Own Product',
        description: 'You cannot make an offer on your own product.',
        color: 'info',
        bgColor: 'info.50',
        borderColor: 'info.200',
      };
    } else {
      return {
        icon: <WarningIcon sx={{ fontSize: 40, color: 'error.main' }} />,
        title: 'Cannot Make Offer',
        description: reason || 'This product is not available for offers.',
        color: 'error',
        bgColor: 'error.50',
        borderColor: 'error.200',
      };
    }
  };

  const statusInfo = getStatusInfo();
  const hasAcceptedOfferPrice = typeof acceptedOfferPrice === 'number' && !Number.isNaN(acceptedOfferPrice);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          overflow: 'hidden',
        }
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          background: `linear-gradient(135deg, ${statusInfo.bgColor} 0%, rgba(255,255,255,0.9) 100%)`,
          borderBottom: `2px solid ${statusInfo.borderColor}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {statusInfo.icon}
              <Typography variant="h5" fontWeight="bold" color={`${statusInfo.color}.main`}>
                {statusInfo.title}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.04)',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: `1px solid ${statusInfo.borderColor}`,
              bgcolor: statusInfo.bgColor,
              mb: 3,
            }}
          >
            <Typography variant="body1" color="text.primary" sx={{ mb: 2, lineHeight: 1.6 }}>
              {statusInfo.description}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <StoreIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">
                Product: <strong>{productName}</strong>
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <MoneyIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">
                Price: <strong>${productPrice.toFixed(2)}</strong>
              </Typography>
            </Box>

            {hasAcceptedOfferPrice && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                <Typography variant="body2" color="text.secondary">
                  Accepted at: <strong>${acceptedOfferPrice.toFixed(2)}</strong>
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">
                Seller: <strong>{sellerName}</strong>
              </Typography>
            </Box>
          </Paper>

          {reason.includes('accepted offer') && (
            <Box sx={{ 
              p: 2, 
              bgcolor: 'info.50', 
              borderRadius: 2, 
              border: '1px solid',
              borderColor: 'info.200',
              mb: 2
            }}>
              <Typography variant="body2" color="info.dark" sx={{ fontWeight: 500 }}>
                💡 <strong>What this means:</strong> The seller has already accepted an offer from another buyer. 
                This product is no longer available for new offers.
              </Typography>
            </Box>
          )}

          {reason.includes('no longer available') && (
            <Box sx={{ 
              p: 2, 
              bgcolor: 'warning.50', 
              borderRadius: 2, 
              border: '1px solid',
              borderColor: 'warning.200',
              mb: 2
            }}>
              <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 500 }}>
                💡 <strong>What this means:</strong> The seller has removed this product from their listings. 
                It's no longer available for purchase or offers.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ 
          px: 3, 
          pb: 3, 
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          '& > *': {
            flex: { xs: 1, sm: 'none' },
            minWidth: { xs: '100%', sm: 'auto' }
          }
        }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              py: 1.5,
              px: 3,
              borderColor: statusInfo.borderColor,
              color: `${statusInfo.color}.main`,
              '&:hover': {
                borderColor: `${statusInfo.color}.dark`,
                bgcolor: statusInfo.bgColor,
              }
            }}
          >
            Continue Chat
          </Button>
          <Button
            variant="contained"
            onClick={() => window.location.href = '/all-products'}
            sx={{
              background: `linear-gradient(135deg, ${statusInfo.color}.main 0%, ${statusInfo.color}.dark 100%)`,
              textTransform: 'none',
              fontWeight: 'bold',
              py: 1.5,
              px: 3,
              boxShadow: 'none',
              '&:hover': {
                background: `linear-gradient(135deg, ${statusInfo.color}.dark 0%, ${statusInfo.color}.main 100%)`,
                boxShadow: `0 4px 12px rgba(${statusInfo.color === 'success' ? '76, 175, 80' : statusInfo.color === 'warning' ? '255, 152, 0' : statusInfo.color === 'info' ? '33, 150, 243' : '244, 67, 54'}, 0.3)`,
              }
            }}
          >
            Browse Other Products
          </Button>
        </DialogActions>
      </motion.div>
    </Dialog>
  );
};

export default OfferStatusPopup;
